import * as React from 'react';

import {
  enumInvoiceSortOption,
  InvoiceSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  DataGrid,
  ListScreenLayout,
  Pagination,
  type PaginationProps,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { invoiceVoidAtom } from '../../_graphql/mutationInvoiceVoid';
import { financeRoutes } from '../../routes';
import { FinanceRootLayout } from '../_layouts/FinanceRootLayout';

import { InvoiceWizard } from './_InvoiceWizard';
import { useInvoicesListColumns } from './hooks/useInvoicesListColumns';
import { InvoicePaymentDrawer } from './InvoicePaymentDrawer';
import { InvoicesListActions } from './InvoicesListActions';
import { InvoicesListFilterBar } from './InvoicesListFilterBar';
import { InvoicesListFloatingToolbar } from './InvoicesListFloatingToolbar';

import * as state from '~/modules/finance/_state/financeInvoices.state';
import { useTranslations } from '~/modules/finance/i18n/useTranslations';
import { InvoiceRecord } from '~/modules/finance/types';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  { asc: InvoiceSortOption; desc: InvoiceSortOption }
> = {
  documentDate: {
    asc: enumInvoiceSortOption.DocumentDateAsc,
    desc: enumInvoiceSortOption.DocumentDateDesc,
  },
  status: {
    asc: enumInvoiceSortOption.StatusAsc,
    desc: enumInvoiceSortOption.StatusDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ INVOICES LIST VIEW ⚛️ ⚛️ ⚛️
//
export const InvoicesListView = () => {
  // Translations
  const t = useTranslations();

  // Navigation
  const [, navigate] = useModuleLocation();

  // Atoms
  const {
    data: { invoices, total } = { invoices: [], total: 0 },
    error,
    refetch: refetchInvoices,
    isFetching,
    loadingState,
  } = useAtomValue(state.$remoteInvoices);
  const viewMode = useAtomValue(state.$viewMode);
  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useAtom(
    state.$isPaymentDrawerOpen,
  );
  const [invoicesToReceive, setInvoicesToReceive] = useAtom(
    state.$invoicesToReceive,
  );
  const [isWizardOpen, setIsWizardOpen] = useAtom(state.$isInvoiceWizardOpen);
  const setCurrentSorting = useSetAtom(state.$currentSorting);

  // Data hooks
  const toast = useToast();

  // Memoized values
  const $voidInvoice = React.useMemo(
    () =>
      invoiceVoidAtom({
        onSuccess: () => {
          refetchInvoices?.();
          setSelectedKeys(new Set());
        },
      }),
    [refetchInvoices, setSelectedKeys],
  );
  const voidInvoice = useSetAtom($voidInvoice);

  const emptyPanelProps = React.useMemo(
    () => ({
      title: t.invoices.list.title,
      description: t.invoices.list.search.placeholder,
      centered: true,
    }),
    [t],
  );

  // Callbacks
  const handleVoid = React.useCallback(
    async (invoice: InvoiceRecord) => {
      if (invoice.status === 'Voided' || invoice.status === 'Paid') {
        toast.error(t.invoices.list.tooltips.cannotVoid);
        return;
      }
      try {
        await voidInvoice({ id: invoice.id });
        toast.success(t.invoices.detail.messages.voided);
      } catch (err) {
        toast.error(
          t.invoices.detail.messages.voidError(
            err instanceof Error ? err.message : t.messages.common.unknownError,
          ),
        );
      }
    },
    [voidInvoice, toast, t],
  );

  const handlePay = React.useCallback(
    (invoiceOrInvoices: InvoiceRecord | InvoiceRecord[]) => {
      const invoicesArray = Array.isArray(invoiceOrInvoices)
        ? invoiceOrInvoices
        : [invoiceOrInvoices];

      const allPending = invoicesArray.every(
        (invoice) => invoice.status === 'Pending',
      );
      if (!allPending) {
        toast.error(t.invoices.list.tooltips.notAllPending);
        return;
      }

      setInvoicesToReceive(invoicesArray);
      setIsPaymentDrawerOpen(true);
    },
    [
      setInvoicesToReceive,
      setIsPaymentDrawerOpen,
      toast,
      t.invoices.list.tooltips,
    ],
  );

  const handleClosePaymentDrawer = React.useCallback(() => {
    setIsPaymentDrawerOpen(false);
    setInvoicesToReceive([]);
  }, [setIsPaymentDrawerOpen, setInvoicesToReceive]);

  const handleWizardClose = React.useCallback(() => {
    setIsWizardOpen(false);
  }, [setIsWizardOpen]);

  const handlePaginationChange = React.useCallback<
    NonNullable<PaginationProps['onChange']>
  >(
    ({ limit: newLimit, offset: newOffset }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumInvoiceSortOption.DocumentDateDesc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          setCurrentSorting([
            direction === 'ascending' ? sortMap.asc : sortMap.desc,
          ]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  const handleInvoiceClick = React.useCallback(
    (invoice: InvoiceRecord) => {
      navigate(financeRoutes.invoiceDetail, {
        params: { id: invoice.id },
      });
    },
    [navigate],
  );

  const { columns } = useInvoicesListColumns({
    onPay: handlePay,
    onVoid: handleVoid,
    onDoubleClick: handleInvoiceClick,
    grouped: viewMode === 'grouped',
  });

  return (
    <FinanceRootLayout className="@InvoicesListView">
      <ListScreenLayout
        filters={<InvoicesListFilterBar />}
        actions={<InvoicesListActions />}
        footer={
          (total ?? 0) > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<InvoiceRecord>
          columns={columns}
          dataSource={invoices}
          rowKey="id"
          aria-label={t.invoices.list.title}
          selectionMode="multiple"
          selectedKeys={selectedKeys}
          onSelectionChange={setSelectedKeys}
          onColumnSort={handleColumnSort}
          fullWidth
          isLoading={loadingState?.isInitialLoading || isFetching}
          hasError={!!error}
          groupBy={
            viewMode === 'grouped'
              ? (invoice) => invoice.accountId ?? '__no_account__'
              : undefined
          }
          errorPanelProps={
            error
              ? {
                  description: t.invoices.list.error(error.message),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <InvoicesListFloatingToolbar />

      <InvoicePaymentDrawer
        isOpen={isPaymentDrawerOpen}
        onClose={handleClosePaymentDrawer}
        invoices={invoicesToReceive || []}
        onSuccess={() => refetchInvoices?.()}
      />

      <InvoiceWizard
        isOpen={isWizardOpen}
        onClose={handleWizardClose}
        onSuccess={() => refetchInvoices?.()}
      />
    </FinanceRootLayout>
  );
};
